import { Immutable } from "immer";
import React, { useEffect, useRef, useState } from "react";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { VerificationGate } from "../../../components/VerificationGate";
import { FORM_FIELD_HAS_ERRORS_SELECTOR } from "../../../forms/classNames";
import {
    FinancingApplicationFields,
    FinancingApplicationPrecursor,
    FinancingPreQualResponse,
} from "../../../models/financing";
import { FormStep, FormStepType } from "../../../models/forms";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import {
    trackCCAppCompleteEvent,
    trackCCAppOpenedEvent,
    trackCCAppStartedEvent,
    trackCCStep1ContactInfoCompleteEvent,
    trackCCStep2FinancialInfoCompleteEvent,
    trackCCStep3IdentificationInfoCompleteEvent,
    trackCCStep4TermsDetailsCompleteEvent,
    trackFormEvent,
} from "../../../utils/analytics";
import { focusElement } from "../../../utils/keyboardFocus";
import { usePageSetting } from "../../../utils/settings";
import {
    ADDRESS_CONFIDENTIALITY_ERROR_CODE,
    FinancingAppType,
    ModalStateType,
} from "../constants";
import {
    AgreeFields,
    ClientErrors,
    ModalFullFormState,
    StepIndex,
    StepIndexFinancingFormNameMap,
    TStepIndex,
} from "../models";
import {
    fullAppNextStep,
    fullAppPrevStep,
    submitFullApplication,
    updateFullAppFormState,
} from "../reducers";
import { pickVerificationPhone } from "../utils";
import { ApplyFormSteps } from "./ApplyFormSteps";
import { getFormSteps } from "./steps";

import styles from "./index.module.scss";

const getCurrentStepErrorCount = (
    currentStep: FormStep<FinancingApplicationFields>,
    clientErrors: ClientErrors,
): number => {
    // Tally up any errors we have
    if (currentStep.stepType === FormStepType.FieldSet) {
        const fieldsToValidate = currentStep.fields;
        return fieldsToValidate.reduce((memo, field) => {
            const fieldClientErrors = clientErrors[field] || [];
            return memo + fieldClientErrors.length;
        }, 0);
    }
    const isTermsStep = currentStep.stepType === FormStepType.OtherJSX;
    if (isTermsStep) {
        const fieldsToValidate = [
            "agree_disclosure",
            "agree_disclosure_joint",
            "esign",
            "esign_joint",
        ] as const satisfies AgreeFields[];
        return fieldsToValidate.reduce((memo, field) => {
            const fieldClientErrors = clientErrors[field] || [];
            return memo + fieldClientErrors.length;
        }, 0);
    }
    return 0;
};

const focusOnFirstError = (form: HTMLFormElement | null | undefined) => {
    // Check error fields and focus first message
    setTimeout(() => {
        if (!form) {
            return;
        }
        const fields = form.querySelectorAll<HTMLElement>(
            FORM_FIELD_HAS_ERRORS_SELECTOR,
        );
        if (fields.length <= 0) {
            return;
        }
        const fieldInput = fields[0].querySelector<HTMLElement>(
            "input, select, textarea",
        );
        if (fieldInput) {
            const window = getWindow();
            if (!window) return;
            focusElement(fieldInput);
            const top = fieldInput.offsetTop - window.innerHeight / 2;
            window.scrollTo(0, top);
        }
    }, 250);
};

const focusOnFirstField = (form: HTMLFormElement | null | undefined) => {
    setTimeout(() => {
        if (!form) {
            return;
        }
        const fields = form.querySelectorAll<HTMLElement>(
            ".form__field.form__field--required, .form__field.form__field--optional",
        );
        if (fields.length <= 0) {
            return;
        }
        const fieldInput = fields[0].querySelector<HTMLElement>(
            "input, select, textarea, button",
        );
        if (fieldInput) {
            focusElement(fieldInput);
        }
    }, 250);
};

const scrollToTop = () => {
    const document = getDocument();
    if (!document) return;

    const checkoutStep = document.querySelector(
        ".checkout-step__payment-enabled-fields",
    );
    if (!checkoutStep) {
        return;
    }
    checkoutStep.scrollIntoView();
};

const getPrecursors = (
    preQualApproval: FinancingPreQualResponse | null,
): FinancingApplicationPrecursor[] => {
    if (!preQualApproval) {
        return [];
    }
    return [
        {
            backend_type: preQualApproval.backend_type,
            reservation_number: preQualApproval.offer_indicator,
            application_id: preQualApproval.response_id,
        },
    ];
};

export const FinancingFullApplyForm = (
    props: Immutable<{
        modalState: ModalFullFormState;
    }>,
) => {
    const appForm = useRef<HTMLFormElement | null>(null);

    const dispatch = useAppDispatch();
    const applicationSource = useAppSelector(
        (state) => state.financing.applicationSource,
    );
    const preQualApproval = useAppSelector(
        (state) => state.financing.preQualApproval,
    );
    const basket = useAppSelector((state) => state.checkout.data.basket);

    const [clientErrors, setClientErrors] = useState<ClientErrors>({});
    const [showErrorMessages, setShowErrorMessages] = useState(false);
    // When true, the form is hidden and the verification gate (email / SMS /
    // voice, per the enabled channels) is shown instead. Set when the user
    // submits the final step (if verification is enabled).
    const [verifying, setVerifying] = useState(false);
    const verificationRequired = !!usePageSetting(
        "financing_email_verification_required",
    );
    const hasStarted = useRef(false);
    const hasFiredRef = useRef(false);

    // On first load, track a start event.
    const trackEvent = (
        event: Parameters<typeof trackFormEvent>[0],
        name: Parameters<typeof trackFormEvent>[1],
    ) => {
        if (appForm.current) {
            trackFormEvent(event, name, appForm.current, basket);
        }
    };
    useEffect(() => {
        if (basket && !hasFiredRef.current) {
            hasFiredRef.current = true;
            const formName =
                StepIndexFinancingFormNameMap[props.modalState.currStepIndex];
            trackEvent("form_start", formName);
            trackCCAppOpenedEvent(basket);
        }
    }, [basket]);

    const isSubmitting =
        props.modalState._tag === ModalStateType.APPLY_SUBMITTING;
    const serverErrors =
        props.modalState._tag === ModalStateType.APPLY_ERROR
            ? props.modalState.errors
            : {};

    const onAppTypeChange = (type: FinancingAppType) => {
        dispatch(
            updateFullAppFormState({
                appType: type,
                currStepIndex: 0,
            }),
        );
    };

    const onValidStateChange = (fieldName: string, errorMessages: string[]) => {
        setClientErrors((s) => ({
            ...s,
            [fieldName]: errorMessages,
        }));
    };

    const onAgree = (event: React.FormEvent<HTMLInputElement>) => {
        const name = event.currentTarget.name;
        if (!AgreeFields.is(name)) {
            return;
        }
        const isChecked = event.currentTarget.checked;
        dispatch(
            updateFullAppFormState({
                values: {
                    [name]: isChecked,
                },
            }),
        );
    };

    const formSteps = getFormSteps(
        props.modalState.appType,
        props.modalState.values,
        {
            backendType: props.modalState.backendType,
            offer: preQualApproval,
            onValidStateChange: onValidStateChange,
            showErrorMessages: showErrorMessages,
            clientErrors: clientErrors,
            onAgree: onAgree,
            onESign: onAgree,
        },
    );
    const currStepIndex = props.modalState.currStepIndex;
    const currentStep = formSteps[currStepIndex];
    const isOnLastStep = currStepIndex === formSteps.length - 1;

    const submitApplication = async (): Promise<void> => {
        const result = await dispatch(
            submitFullApplication({
                applicationSource: applicationSource,
                backendType: props.modalState.backendType,
                precursors: getPrecursors(preQualApproval),
                appType: props.modalState.appType,
                values: props.modalState.values,
                currStepIndex: props.modalState.currStepIndex,
                disabledSteps: props.modalState.disabledSteps,
            }),
        );
        if (submitFullApplication.fulfilled.match(result)) {
            // Address confidentiality rejections resolve with the server's
            // message so it can be shown, but the application wasn't completed.
            const isAddressConfidentialityError =
                result.payload._tag === ModalStateType.APPLY_ERROR &&
                result.payload.errors.code ===
                    ADDRESS_CONFIDENTIALITY_ERROR_CODE;
            if (!isAddressConfidentialityError) {
                trackCCAppCompleteEvent(basket);
            }
        }
    };

    const onNextStep = async (
        event:
            | React.FormEvent<HTMLButtonElement>
            | React.FormEvent<HTMLFormElement>,
    ): Promise<void> => {
        event.preventDefault();

        // Stop the submit event from bubbling up any further. This is necessary because the Apply form, while not nested
        // inside any other forms according to the DOM hierarchy, *is* nested within a checkout form according to the React
        // component hierarchy. This causes checkout to try and submit when submitting the Apply form. Long term, we
        // should try and remove this nesting.
        // See also: https://github.com/erikras/redux-form/issues/3701
        event.stopPropagation();

        // Tally up any client-side errors we have
        const errorCount = currentStep
            ? getCurrentStepErrorCount(currentStep, clientErrors)
            : 0;

        // Don't go to next step if the current step is invalid. Just turn on error display.
        if (errorCount > 0) {
            setShowErrorMessages(true);
            focusOnFirstError(appForm.current);
            return;
        }

        if (currentStep?.stepType === FormStepType.FieldSet) {
            trackEvent(
                "form_submit",
                StepIndexFinancingFormNameMap[currStepIndex],
            );
            if (currStepIndex === StepIndex.MAIN_CONTACT) {
                trackCCStep1ContactInfoCompleteEvent(basket);
            } else if (currStepIndex === StepIndex.MAIN_INCOME) {
                trackCCStep2FinancialInfoCompleteEvent(basket);
            } else if (currStepIndex === StepIndex.MAIN_IDENT) {
                trackCCStep3IdentificationInfoCompleteEvent(basket);
            }
        }

        // If we're on the last step, submit the app to the server.
        if (isOnLastStep) {
            trackEvent(
                "form_submit",
                StepIndexFinancingFormNameMap[currStepIndex],
            );
            trackCCStep4TermsDetailsCompleteEvent(basket);
            // Before the application can be submitted, the user must verify
            // ownership of their contact info (server-enforced fraud control).
            // Swap the form out for the verification gate; once verified it
            // triggers the actual submission. If the account already verified
            // (or is exempt), the gate calls onVerified immediately.
            if (verificationRequired) {
                setVerifying(true);
                return;
            }
            await submitApplication();
            return;
        }

        // Proceed to next step
        scrollToTop();
        setShowErrorMessages(false);
        dispatch(fullAppNextStep({ totalStepCount: formSteps.length }));
        focusOnFirstField(appForm.current);
    };

    const onPrevStep = () => {
        scrollToTop();
        setShowErrorMessages(false);
        dispatch(fullAppPrevStep());
    };

    const onChange = (name: FinancingApplicationFields, value: string) => {
        if (!hasStarted.current) {
            hasStarted.current = true;
            trackCCAppStartedEvent(basket);
        }
        dispatch(
            updateFullAppFormState({
                values: {
                    [name]: value,
                },
            }),
        );
    };

    const onSummaryEdit = (step: number) => {
        if (TStepIndex.is(step)) {
            dispatch(
                updateFullAppFormState({
                    currStepIndex: step,
                }),
            );
        }
    };

    if (verifying) {
        return (
            <div className={styles.verificationWrapper}>
                <VerificationGate
                    email={
                        props.modalState.values.main_applicant__email_address
                    }
                    phone={
                        // Prefer the mobile number, fall back to the (always
                        // collected, required) home number. pickVerificationPhone
                        // skips a country-code-only value ("+1") that the phone
                        // widget leaves in a focused-but-unfilled field — a plain
                        // `||` chain would wrongly send that "+1" instead of the
                        // real number in a later field.
                        pickVerificationPhone(
                            props.modalState.values
                                .main_applicant__mobile_phone,
                            props.modalState.values.main_applicant__home_phone,
                        )
                    }
                    onVerified={() => {
                        setVerifying(false);
                        void submitApplication();
                    }}
                    onCancel={() => {
                        setVerifying(false);
                    }}
                />
            </div>
        );
    }

    return (
        <ApplyFormSteps
            ref={appForm}
            formSteps={formSteps}
            currStepIndex={currStepIndex}
            disabledSteps={props.modalState.disabledSteps}
            isJoint={props.modalState.appType === FinancingAppType.JOINT}
            isLoading={isSubmitting}
            backendType={props.modalState.backendType}
            values={props.modalState.values}
            showErrorMessages={showErrorMessages}
            serverErrors={serverErrors}
            clientErrors={clientErrors}
            onAppTypeChange={onAppTypeChange}
            onNextStep={onNextStep}
            onPrevStep={onPrevStep}
            onChange={onChange}
            onValidStateChange={onValidStateChange}
            onSummaryEdit={onSummaryEdit}
        />
    );
};
